import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { getGroupPriorityConfig, updateGroupPriorityConfig } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// i18n keys for the fixed tier display names (reused from the worklist toolbar).
const TIER_LABEL_KEY = {
    stat: 'toolbar.priorityStat',
    urgent: 'toolbar.priorityUrgent',
    routine: 'toolbar.priorityRoutine',
};
/**
 * Group-admin editor for the consultation priorities the group offers and their
 * turnaround times. Submitting clinics see only the enabled tiers (with these
 * TATs) in the case-submission priority dropdown.
 */
export function PrioritiesSection({ groupId }) {
    const { t } = useTranslation('teleconsultation');
    const { toast } = useToast();
    const { data } = useQuery({
        queryKey: ['tc-priority-config', groupId],
        queryFn: () => getGroupPriorityConfig(groupId),
        enabled: !!groupId,
    });
    // Server data + a per-key edit overlay (no effect needed to "sync" on load).
    const [edits, setEdits] = useState({});
    const rows = (data ?? []).map((r) => ({ ...r, ...(edits[r.key] ?? {}) }));
    const patch = (key, p) => setEdits((prev) => ({ ...prev, [key]: { ...(prev[key] ?? {}), ...p } }));
    const save = useMutation({
        mutationFn: () => updateGroupPriorityConfig(groupId, rows),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tc-priority-config', groupId] });
            queryClient.invalidateQueries({ queryKey: ['available-groups'] });
            setEdits({});
            toast({ title: t('priorities.saved') });
        },
        onError: (e) => toast({ title: e.message || t('priorities.saveError'), variant: 'destructive' }),
    });
    if (!data)
        return null;
    const anyEnabled = rows.some((r) => r.enabled);
    const allTatValid = rows.every((r) => r.tatMinutes > 0);
    return (_jsxs("section", { className: "border-border bg-panel-chrome space-y-4 rounded-md border p-4", children: [_jsxs("div", { children: [_jsxs("p", { className: "flex items-center gap-1.5 text-sm font-medium", children: [_jsx(Clock, { className: "h-4 w-4" }), t('priorities.title')] }), _jsx("p", { className: "text-muted-foreground text-xs", children: t('priorities.hint') })] }), _jsx("div", { className: "space-y-3", children: rows.map((r) => (_jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Switch, { checked: r.enabled, onCheckedChange: (v) => patch(r.key, { enabled: v }), "aria-label": t('priorities.enabledFor', { key: r.key }) }), _jsx("span", { className: "text-muted-foreground w-16 text-xs font-semibold uppercase", children: r.key }), _jsx(Input, { className: "h-8 w-40", placeholder: TIER_LABEL_KEY[r.key] ? t(TIER_LABEL_KEY[r.key]) : r.key, value: r.label ?? '', onChange: (e) => patch(r.key, { label: e.target.value || null }) }), _jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Input, { type: "number", min: 1, className: "h-8 w-24", value: r.tatMinutes, onChange: (e) => patch(r.key, { tatMinutes: parseInt(e.target.value, 10) || 0 }) }), _jsx("span", { className: "text-muted-foreground text-xs", children: t('priorities.minutes') })] })] }, r.key))) }), !anyEnabled && _jsx("p", { className: "text-destructive text-xs", children: t('priorities.atLeastOne') }), _jsx(Button, { size: "sm", disabled: !anyEnabled || !allTatValid || save.isPending, onClick: () => save.mutate(), children: t('priorities.save') })] }));
}
